import * as imgUtil from "@shared/imgUtil.js";
import { renderSettings, croppedDrawRect } from "@shared/RenderSettings.js";
import { Product } from "@shared/Product.js";
import { CYODesign } from "@shared/CYODesign.js";
import { Edge } from "@shared/BorderLoc.js";
import { Rect } from "@shared/Rect.js";
import { ArrayUtil } from "@shared/utility.js";
import { ContentRendererUtils } from "@shared/ContentRendererUtils.js";
import { dlog, debugDesc } from "@shared/utility.js";

export class ContentRendererUserImage {
    
    static drawFullImage(ctx, userImgVariant, device, canvasWidth, canvasHeight, cyoDesign) {
        dlog(">> _drawFullImage");
        
        // For full image style, we want to:
        // 1. Fill the entire canvas (not just fit to it)
        // 2. Preserve the full image data for gesture scaling (no cropping)
        // 3. Center the image properly in the final composite
        // 4. Respect bleed area insets to avoid restricted device areas
        
        // Get bleed area insets and calculate margins
        // NB: we use bleed area for images but safe area for stickers.
        const insets = device.bleedInsets;
        const topMargin = ArrayUtil.top(insets) * canvasHeight;
        const rightMargin = ArrayUtil.right(insets) * canvasWidth;
        const bottomMargin = ArrayUtil.bottom(insets) * canvasHeight;
        const leftMargin = ArrayUtil.left(insets) * canvasWidth;
        
        // Calculate the bleed area dimensions (canvas minus insets)
        const bleedCanvasWidth = canvasWidth - (leftMargin + rightMargin);
        const bleedCanvasHeight = canvasHeight - (topMargin + bottomMargin);
        
        // Calculate the scale needed to fill (not fit) the bleed area
        // Math.max ensures the image covers the entire bleed canvas area
        const fillScale = Math.max(bleedCanvasHeight / userImgVariant.height, bleedCanvasWidth / userImgVariant.width);
        
        // Calculate what the full scaled image dimensions would be
        const fullScaledWidth = userImgVariant.width * fillScale;
        const fullScaledHeight = userImgVariant.height * fillScale;
        
        // Create expanded canvas to hold the full image without clipping
        // This ensures we preserve the full image data for gesture interactions
        const expandedWidth = Math.max(canvasWidth, fullScaledWidth);
        const expandedHeight = Math.max(canvasHeight, fullScaledHeight);
        
        ctx.canvas.width = expandedWidth;
        ctx.canvas.height = expandedHeight;
        ctx.clearRect(0, 0, expandedWidth, expandedHeight);
        
        // Calculate where to draw the image in the expanded canvas
        const drawRect = new Rect();
        drawRect.width = fullScaledWidth;
        drawRect.height = fullScaledHeight;
        
        // Center the scaled image in the expanded canvas, accounting for bleed area insets
        // The image should be centered within the bleed area, not the full canvas
        const bleedCenterX = leftMargin + bleedCanvasWidth / 2;
        const bleedCenterY = topMargin + bleedCanvasHeight / 2;
        
        const baseDrawX = (expandedWidth - fullScaledWidth) / 2;
        const baseDrawY = (expandedHeight - fullScaledHeight) / 2;
        const bleedOffsetX = bleedCenterX - canvasWidth / 2;
        const bleedOffsetY = bleedCenterY - canvasHeight / 2;
        
        drawRect.x = baseDrawX + bleedOffsetX;
        drawRect.y = baseDrawY + bleedOffsetY;
        
        // Calculate viewport offset for the Compositor
        // This tells the Compositor how to shift the expanded canvas to center the visible portion
        // within the original canvas dimensions, accounting for bleed area positioning
        const baseViewportOffsetX = (fullScaledWidth - canvasWidth) / 2;
        const baseViewportOffsetY = (fullScaledHeight - canvasHeight) / 2;
        const viewportOffsetX = baseViewportOffsetX + bleedOffsetX;
        const viewportOffsetY = baseViewportOffsetY + bleedOffsetY;
        
        // Debug logging - can be removed in production
        dlog(`drawFullImage: canvas(${canvasWidth}x${canvasHeight}) -> bleed area(${bleedCanvasWidth}x${bleedCanvasHeight}) -> expanded(${expandedWidth}x${expandedHeight})`);
        dlog(`drawFullImage: image scaled to (${fullScaledWidth}x${fullScaledHeight}) with viewport offset (${viewportOffsetX}, ${viewportOffsetY})`);
        
        // Draw debug background if enabled
        if (cyoDesign.debugOptions.drawBgs) {
            ctx.fillStyle = "rgba(89, 196, 246, 0.3)"; // Translucent blue
            ctx.fillRect(drawRect.x, drawRect.y, drawRect.width, drawRect.height);
        }
                
        ContentRendererUtils.placeImage(ctx, 
            userImgVariant,
            drawRect,
            cyoDesign.subjectOutlines,
            cyoDesign);
            
        // Store metadata to help the layer system properly position this expanded canvas
        // These properties are used by the Compositor and InteractiveCanvas to:
        // - Detect expanded canvases (_isExpandedCanvas)
        // - Know the original intended canvas dimensions
        // - Apply the correct viewport offset for centering
        // For viewport offset: only use non-zero offsets when the scaled image is larger than canvas
        // If image is smaller than canvas, our drawRect positioning already handles centering with bleed area
        const finalViewportOffsetX = fullScaledWidth > canvasWidth ? baseViewportOffsetX : 0;
        const finalViewportOffsetY = fullScaledHeight > canvasHeight ? baseViewportOffsetY : 0;
                
        ctx.canvas._isExpandedCanvas = true;
        ctx.canvas._originalCanvasWidth = canvasWidth;
        ctx.canvas._originalCanvasHeight = canvasHeight;
        ctx.canvas._viewportOffsetX = finalViewportOffsetX;
        ctx.canvas._viewportOffsetY = finalViewportOffsetY;
    }
    
    static async drawSnapshot(ctx, userImgVariant, device, canvasWidth, canvasHeight, cyoDesign, randomState) {
        const product = device.productCategory;
        
        const borderWidth = renderSettings(product, "snapshotBorderWidth");
        const inset = canvasWidth * renderSettings(product, "snapshotInsetRatio");
        const extraOffsetY = renderSettings(product, "snapshotExtraOffsetY");
        const rotationRandomness = renderSettings(product, "snapshotRotationRandomnessDeg"); // currently 5 degs for all products
        const minRotationDegs = 3;
            
        // Get a random rotation angle in radians, ensuring minimum rotation of 10 degrees
        let rotationDegs;
        if (rotationRandomness >= minRotationDegs) {
            // Standard case: generate random rotation with minimum constraint
            do {
                rotationDegs = imgUtil.linearRandom(-rotationRandomness, rotationRandomness, randomState);
            } while (Math.abs(rotationDegs) < minRotationDegs);
        } else {
            // Edge case: rotation range is too small, use minimum rotation with random sign
            rotationDegs = randomState.randomBool() ? minRotationDegs : -minRotationDegs;
        }
        
        const rotAngleRads = rotationDegs * (Math.PI / 180);
    
        dlog(`rotationDegs: ${rotationDegs}`)
    
        // Calculate scaling needed to fit the rotated square
        const cos1 = Math.abs(Math.cos(rotAngleRads - Math.PI / 4));
        const cos2 = Math.abs(Math.cos(rotAngleRads + Math.PI / 4));
        const cosMax = Math.max(cos1, cos2);
        const rotatedSizeRatio = Math.SQRT2 * cosMax;
    
        // Determine the available size within the canvas, accounting for insets
        const availableWidth = canvasWidth - 2 * inset;
        const availableHeight = canvasHeight - 2 * inset;
        const availableSize = Math.min(availableWidth, availableHeight);
    
        // Calculate the size of the square before rotation
        const snapshotSize = availableSize / rotatedSizeRatio;
    
        // Center coordinates
        const centerX = canvasWidth / 2;
        const centerY = canvasHeight / 2 + extraOffsetY;
    
        dlog(">> _drawSnapshot");

        // Save the current context state
        ctx.save();
    
        // Move the origin and apply rotation
        ctx.translate(centerX, centerY);
        ctx.rotate(rotAngleRads);
    
        // Draw the outer white square (border)
        ctx.fillStyle = "white";
        ctx.fillRect(-snapshotSize / 2, -snapshotSize / 2, snapshotSize, snapshotSize);
    
        // Calculate the inner square size
        const innerSize = snapshotSize - 2 * borderWidth;
    
        // Set the clipping region to the inner square
        ctx.beginPath();
        ctx.rect(
            -snapshotSize / 2 + borderWidth,
            -snapshotSize / 2 + borderWidth,
            innerSize,
            innerSize
        );
        ctx.clip();
    
        // Calculate scaling factor to fill the inner square
        const scale = Math.max(innerSize / userImgVariant.width, innerSize / userImgVariant.height);
    
        // Calculate the dimensions of the scaled image
        const scaledWidth = userImgVariant.width * scale;
        const scaledHeight = userImgVariant.height * scale;
    
        // Calculate position to center the image within the inner square
        const imgX = -snapshotSize / 2 + borderWidth - (scaledWidth - innerSize) / 2;
        const imgY = -snapshotSize / 2 + borderWidth - (scaledHeight - innerSize) / 2;
    
        // Draw debug background if enabled (only for the image area within the clipped region)
        if (cyoDesign.debugOptions.drawBgs) {
            ctx.fillStyle = "rgba(89, 196, 246, 0.3)"; // Translucent blue
            ctx.fillRect(imgX, imgY, scaledWidth, scaledHeight);
        }

        ctx.drawImage(userImgVariant, imgX, imgY, scaledWidth, scaledHeight);
    
        ctx.restore();
    }
    
    static async drawRemoveBackgroundCenterOrBottom(ctx, userImgVariant, userImageFlatEdges, device, canvasWidth, canvasHeight, cyoDesign) {
        const product = device.productCategory;
        const insets = device.bleedInsets;
        const margins = device.userImageMargins;
        const flatEdges = userImageFlatEdges;
        
        const hasFlatLeft = flatEdges.includes(Edge.left);
        const hasFlatRight = flatEdges.includes(Edge.right);
        const hasFlatLeftOrRight = hasFlatLeft || hasFlatRight;
        const hasFlatTop = flatEdges.includes(Edge.top);
        const hasFlatBottom = flatEdges.includes(Edge.bottom);
        const hasFlatTopOrBottom = hasFlatTop || hasFlatBottom;
        
        const subjectPlacementBottom = cyoDesign.subjectPlacement == CYODesign.Placement.bottom;
        const subjectPlacementCenter = cyoDesign.subjectPlacement == CYODesign.Placement.center;
        
        const imgUploadAspect = userImgVariant.width / userImgVariant.height;

        dlog(">> _drawRemoveBackgroundCenterOrBottom");
        
        // Default margin ratios...
        var topMarginRatio = ArrayUtil.top(insets) + ArrayUtil.top(margins);
        var rightMarginRatio = ArrayUtil.right(insets) + ArrayUtil.right(margins);
        var bottomMarginRatio = ArrayUtil.bottom(insets) + ArrayUtil.bottom(margins);
        var leftMarginRatio = ArrayUtil.left(insets) + ArrayUtil.left(margins);

        var topMargin, rightMargin, bottomMargin, leftMargin;
        var croppedCanvasWidth, croppedCanvasHeight, canvasAspect;

        function calculateMarginPixels() {
            topMargin = topMarginRatio * canvasHeight;
            rightMargin = rightMarginRatio * canvasWidth;
            bottomMargin = bottomMarginRatio * canvasHeight;
            leftMargin = leftMarginRatio * canvasWidth;
        }
        
        // get canvas dims subtracting margins
        function calculateCroppedCanvas() {
            croppedCanvasWidth = canvasWidth - (rightMargin + leftMargin);
            croppedCanvasHeight = canvasHeight - (topMargin + bottomMargin);
            canvasAspect = croppedCanvasWidth / croppedCanvasHeight;
        }
        
        // Start scenario drawRect local getters
        
        function drawRectForBottomPlacement() {
            dlog(">> drawRectForBottomPlacement");
            
            // Always justify to bottom with "bottom" placement
            bottomMarginRatio = ArrayUtil.bottom(insets);
        
            // Override defaults for flat edges
            if (hasFlatTop) {
                topMarginRatio = ArrayUtil.top(insets);
            }
            if (hasFlatRight) {
                rightMarginRatio = ArrayUtil.right(insets);
            }
            if (hasFlatLeft) {
                leftMarginRatio = ArrayUtil.left(insets);
            }
        
            // For case, top edge will always be under the camera + margin
            if (device.cameraCutoutFrame) {
                const cameraCutoutFrame = Rect.fromArray(device.cameraCutoutFrame);
                topMarginRatio = cameraCutoutFrame.maxY + ArrayUtil.top(margins);
            }
            
            calculateMarginPixels();
            calculateCroppedCanvas();
            
            const drawRect = new Rect();
            
            if (imgUploadAspect < canvasAspect) {
                dlog("portrait layout");
                // img is taller than canvas
                drawRect.width = croppedCanvasWidth;
                drawRect.height = drawRect.width / imgUploadAspect;
            } else {
                // img is wider; scale by width
                dlog("landscape layout");
                drawRect.width = croppedCanvasWidth;
                drawRect.height = drawRect.width / imgUploadAspect;
            }
            
            // center horizontally within cropped canvas
            drawRect.x = leftMargin + croppedCanvasWidth / 2 - drawRect.width / 2;
            
            if (imgUploadAspect < canvasAspect) {
                // Anchor top
                drawRect.y = topMargin;
            } else {
                // Anchor bottom
                drawRect.y = canvasHeight - drawRect.height - bottomMargin;
            }
                        
            return drawRect;
        }

        function drawRectForGripCenterPlacement() {
            dlog(">> _drawRectForGripCenterPlacement");

            if (hasFlatBottom && !hasFlatTop) {
                // #161: Grip: photo/center: w flat bottom still use some margin on top
                // "split the difference"
                topMarginRatio = ArrayUtil.top(insets) + ArrayUtil.top(margins)/2;
                bottomMarginRatio = ArrayUtil.bottom(insets);
            } else {
                // If flat edge, ignore margins
                if (hasFlatTopOrBottom) {
                    topMarginRatio = ArrayUtil.top(insets);
                    bottomMarginRatio = ArrayUtil.bottom(insets);
                }
            }
            if (hasFlatLeftOrRight) {
                rightMarginRatio = ArrayUtil.right(insets);
                leftMarginRatio = ArrayUtil.left(insets);
            }
            
            calculateMarginPixels();
            calculateCroppedCanvas();
            
            const drawRect = new Rect();
            
            if (imgUploadAspect < canvasAspect) {
                if (hasFlatLeftOrRight) {
                    // if centered then fully justify the image left and right
                    drawRect.width = croppedCanvasWidth;
                    drawRect.height = drawRect.width / imgUploadAspect;
                } else {
                    // img is taller; scale by height
                    drawRect.height = croppedCanvasHeight;
                    drawRect.width = drawRect.height * imgUploadAspect;
                }
            } else {
                // img is wider; scale by width
                drawRect.width = croppedCanvasWidth;
                drawRect.height = drawRect.width / imgUploadAspect;
            }
            
            // #129: Also circle mask for grip when center
            // While circle masking flat bottoms is usually done at the beginning of draw(), this is a better place for grip
            // because we can accurately calculate the correct radius.
            const radius = croppedCanvasWidth / 2;
            userImgVariant = imgUtil.resizeImageOrCanvasForHeight(userImgVariant, drawRect.height);
            userImgVariant = imgUtil.maskImageWithCircle(userImgVariant, radius);
            
            // center horizontally within cropped canvas
            drawRect.x = leftMargin + croppedCanvasWidth / 2 - drawRect.width / 2;
            // center vertically within cropped canvas
            drawRect.y = canvasHeight - croppedCanvasHeight + (croppedCanvasHeight - drawRect.height) / 2 - bottomMargin;
            
            // exception: if any side is flat and the img is taller than canvas, then anchor from top (i.e. prefer to cut off bottom instead of top where faces are likely). otherwise continue to center vertically.
            if (hasFlatLeftOrRight && imgUploadAspect < canvasAspect) {
                drawRect.y = canvasHeight / 2 - croppedCanvasHeight / 2;
            }
            
            return drawRect;
        }
        
        function drawRectForCaseOrWalletCenterPlacement() {
            dlog(">> _drawRectForCaseOrWalletCenterPlacement");
            
            // Override defaults for flat edges
            if (hasFlatRight) {
                rightMarginRatio = ArrayUtil.right(insets);
            }
            if (hasFlatLeft) {
                leftMarginRatio = ArrayUtil.left(insets);
            }
            
            // For case, top edge will always be under the camera + margin
            if (device.cameraCutoutFrame) {
                const cameraCutoutFrame = Rect.fromArray(device.cameraCutoutFrame);
                topMarginRatio = cameraCutoutFrame.maxY + ArrayUtil.top(margins);
            }

            calculateMarginPixels();
            calculateCroppedCanvas();
                    
            const drawRect = new Rect();
            
            if (imgUploadAspect < canvasAspect) {
                // img is taller than canvas
                drawRect.height = croppedCanvasHeight;
                drawRect.width = drawRect.height * imgUploadAspect;
            } else {
                // img is wider; scale by width
                drawRect.width = croppedCanvasWidth;
                drawRect.height = drawRect.width / imgUploadAspect;
            }
            
            // center horizontally within cropped canvas
            drawRect.x = leftMargin + croppedCanvasWidth / 2 - drawRect.width / 2;
            // center vertically within cropped canvas
            drawRect.y = (croppedCanvasHeight / 2 - drawRect.height / 2) + topMargin;

            return drawRect;
        }
        // End scenario drawRect local getters

        var drawRect;
        if (subjectPlacementBottom) {
            drawRect = drawRectForBottomPlacement();

            // Use expanded canvas to preserve full image for gesture panning
            // See #210 'Bottom' image placement should be translated, not cut off
            // Check if the drawRect extends beyond canvas bounds in any direction
            const extendsBelow = drawRect.y + drawRect.height > canvasHeight;
            const extendsAbove = drawRect.y < 0;
            const extendsRight = drawRect.x + drawRect.width > canvasWidth;
            const extendsLeft = drawRect.x < 0;
            const needsExpansion = extendsBelow || extendsAbove || extendsRight || extendsLeft;

            if (needsExpansion) {
                const scaledWidth = drawRect.width;
                const scaledHeight = drawRect.height;
                const expandedWidth = Math.max(canvasWidth, scaledWidth + leftMargin + rightMargin);
                const expandedHeight = Math.max(canvasHeight, scaledHeight + topMargin + bottomMargin);

                ctx.canvas.width = expandedWidth;
                ctx.canvas.height = expandedHeight;
                ctx.clearRect(0, 0, expandedWidth, expandedHeight);

                // Reposition image in expanded canvas
                const newDrawX = (expandedWidth - scaledWidth) / 2;

                // Preserve same anchor logic: tall images anchor top, wide images anchor bottom
                let newDrawY;
                if (imgUploadAspect < canvasAspect) {
                    newDrawY = topMargin;
                } else {
                    newDrawY = expandedHeight - scaledHeight - bottomMargin;
                }

                drawRect.x = newDrawX;
                drawRect.y = newDrawY;

                // Calculate viewport offset for correct initial view
                const viewportOffsetX = (expandedWidth - canvasWidth) / 2;
                let viewportOffsetY;
                if (imgUploadAspect < canvasAspect) {
                    viewportOffsetY = 0;
                } else {
                    viewportOffsetY = expandedHeight - canvasHeight;
                }

                ctx.canvas._isExpandedCanvas = true;
                ctx.canvas._originalCanvasWidth = canvasWidth;
                ctx.canvas._originalCanvasHeight = canvasHeight;
                ctx.canvas._viewportOffsetX = viewportOffsetX;
                ctx.canvas._viewportOffsetY = viewportOffsetY;

                // Calculate rotation pivot as the center of the visible image portion.
                // The visible portion is where the image intersects the original canvas bounds.
                // This ensures rotation feels natural - pivoting around what the user sees.
                const imageTopInOutput = newDrawY - viewportOffsetY;
                const imageBottomInOutput = imageTopInOutput + scaledHeight;
                const visibleTop = Math.max(0, imageTopInOutput);
                const visibleBottom = Math.min(canvasHeight, imageBottomInOutput);
                ctx.canvas._rotationPivotX = canvasWidth / 2;
                ctx.canvas._rotationPivotY = (visibleTop + visibleBottom) / 2;
            }
        } else if (product == Product.grip && subjectPlacementCenter) {
            drawRect = drawRectForGripCenterPlacement();
        } else if ((product == Product.case || product == Product.wallet) && subjectPlacementCenter) {
            drawRect = drawRectForCaseOrWalletCenterPlacement();
        } else {
            console.error("*** Unhandled scenario");
        }

        // error if userImgVariant isn't already a canvas
        if (cyoDesign.debugOptions.drawBgs) {
            ContentRendererUtils.debugAddBg(userImgVariant, ContentRendererUtils.debugBgColor1);
        }

        ContentRendererUtils.placeImage(ctx,
            userImgVariant,
            drawRect,
            cyoDesign.subjectOutlines,
            cyoDesign);
    }
}